// The 選品 screen's own arithmetic and wording. Pure — no React, no I/O — so every sentence the
// owner reads can be asserted in a test without rendering anything.
//
// Magnus 2026-09-16: the engine no longer chooses the assortment. It PROPOSES; he answers yes/no;
// he can add anything it never proposed; that list is then the default. Three rules fall out of
// that and are enforced here rather than hoped for:
//   1. The list gates ordering — a style that is not approved is shown, never ordered. (backend)
//   2. Over capacity is a SOFT warning: a number and a ranked 建議帶回 hint. It never blocks a
//      button and never drops a row (`overLine` returns a sentence, not a veto).
//   3. SKU-level, not variant-level: he picks styles, the engine picks colours, sizes and depth.
import { isWarehouseLane, sourcingBucket } from './sourcing';
import {
  CounterListDecision, CounterListProposalKind, CounterListStatus, RestockV2ExcludedStyle,
  RestockV2List, RestockV2ListApproved,
  RestockV2ListBatchBody,
  RestockV2ListBringBack, RestockV2ListDecideBody, RestockV2ListExcludedVariant,
  PoolSeason, RestockV2ListPoolFacets, RestockV2ListPoolItem, RestockV2ListProposal,
  RestockV2ListSearchRow,
  RestockV2ListSeedBody, RestockV2ListSkuBody, RestockV2ListVariant, RestockV2ListVariantBody,
  RestockV2ListVariantReaddBody, RestockV2Plan, TierKey,
} from './types';

// One proposal card answers one of four questions, and the verbs differ because the questions do.
// 'pool' is the 看更多 row: the SAME two verbs as 'add' (the decision is identical), on a style the
// engine deliberately did NOT propose — so it is its own kind, never an 'add' with a different
// wrapper, because the card has to say that out loud.
export type ProposalKindKey = 'add' | 'remove' | 'stock' | 'pool';

// `Number(null)` is 0, so a count the engine did not measure would otherwise be reported as a
// measured zero. 「門市30天 0 件」 and 「we have no counter figure」 are different claims.
const numberOr = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// A no-break space, so a line can break BETWEEN facts but never inside one. Without it the numbers
// line broke as 「客人問過 秋」 / 「外套」 and orphaned a bare 「·」 onto the next line.
const NB = ' ';

/** Compare on the glyphs only: the headline writes 「線上 14 天 3 件」, a chip writes 「線上14天 3 件」. */
const squash = (text: string): string => text.replace(/[\s ·・，,]/g, '');

// ---- the verbs ------------------------------------------------------------------------------
// 建議加入 answers yes/no; 建議換下 answers 換下/留著. Same two-button shape, honest words: a
// 建議換下 card is already ON the list, so 「加入清單」 would be nonsense there.
export const LIST_APPROVE_LABEL = '加入清單';
export const LIST_REJECT_LABEL = '不要這款';
export const LIST_DROP_LABEL = '換下';
export const LIST_KEEP_LABEL = '留著';
export const LIST_REMOVE_LABEL = '移除';
export const LIST_ADD_BACK_LABEL = '加回';
export const LIST_ADD_LABEL = '加入';
export const LIST_BATCH_LABEL = '全部照建議';
export const LIST_SEED_LABEL = '用現在架上的貨當清單';

// The one hard warning of the 加入商品 picker: a SKU with no 下單連結 can be on the list but nobody
// can order it, so the row says so and stays addable anyway.
//
// It used to say 沒有登記進貨店家 (Magnus 2026-09-18: 「tag doesn't matter, there is order links」).
// The 進貨店家 tag only GROUPS lines under a shop heading; a style without one orders perfectly
// well. What stops an order is a missing `product_links` row, so that is what the warning names.
export const LIST_NO_LINK_WARNING = '沒有下單連結，訂不到';

// 不要這個顏色／尺寸 — the narrower verb, added 2026-09-17. 不要這款 is UNCHANGED and still means the
// whole style; this one takes out one colour or one size and leaves the style on the list.
export const LIST_VARIANT_REJECT_LABEL = '不要這個顏色／尺寸';
// Every colour of a style vetoed one at a time. The row STAYS — it owns no depth and no rack goal,
// its pieces are all in 帶回, and the server's own lane_reason says so — but it is the only place
// the 加回 chips live, so hiding it would make the decision unreachable.
export const LIST_ALL_VETOED_BADGE = '全部顏色都排除';
export const LIST_VARIANT_NOTE = '按顏色／尺寸旁邊的 ×，只拿掉那一個；整款都不要才按「不要這款」。拿掉的顏色件數會從這款的件數裡扣掉，不會分給別的顏色。';
export const LIST_VARIANT_EXCLUDED_NOTE = '這些顏色／尺寸不會補、不會上架，整款還在清單上。';

// 全部照建議 is ADDITIVE ONLY: a 全選／全部取消 was deliberately removed from 訂貨 on 2026-09-15
// because one mis-tap vetoed the whole order for Adam too, so nothing here can reject in bulk.
// It also sweeps ONLY the engine queue. A 你已經有貨 row is a different decision — it puts
// pieces the engine judged not worth showing back onto a capacity-limited rail — so it is one tap
// each, deliberately. Same reason 全選 was removed from 訂貨 on 2026-09-15: the bulk control is
// safe only where every row in it is the same kind of yes.
export const LIST_BATCH_NOTE = '一次把上面的建議全部加入清單。不會幫你排除任何一款，也不會動「你已經有貨」那一區。';

export const LIST_STOCK_TITLE = '你已經有貨';
export const LIST_STOCK_BADGE = '已有貨';
export const LIST_STOCK_NOTE = '這幾款引擎沒有選，但門市或辦公室已經有貨。沒有加入清單，這些件數就不會出現在「今天」的上架和去拿，會一直放著。一款一款決定，不要整批按。';

export const LIST_NOT_READY = '選品清單還沒建立，這個畫面先停用。其他三個畫面照常運作。';
export const LIST_EMPTY_NOTE = '清單還是空的。按下面的按鈕把現在架上的貨當成第一版清單，再慢慢刪；或直接從上面的建議一款一款按。';
export const LIST_NO_PROPOSALS = '目前沒有要你決定的款，清單就是引擎現在的意見。';
export const LIST_SEARCH_EMPTY = '找不到這個商品編號或名稱。';

// ---- reading the block ------------------------------------------------------------------------

export function counterList(plan: RestockV2Plan | null | undefined): RestockV2List | null {
  return plan?.list || null;
}

/** The table exists AND the engine could compute against it. Anything else = the degraded screen. */
export function listReady(plan: RestockV2Plan | null | undefined): boolean {
  return counterList(plan)?.ready === true;
}

export function proposalsToAdd(list: RestockV2List | null): RestockV2ListProposal[] {
  return list?.proposals?.add || [];
}

/** A backend that predates the split sends no `proposal_kind`; those rows are engine picks. */
export function proposalKind(row: RestockV2ListProposal): CounterListProposalKind {
  return row.proposal_kind === 'stock' ? 'stock' : 'engine';
}

/** 建議加入 — the engine's recommendations. What 全部照建議 sweeps. */
export function engineProposals(list: RestockV2List | null): RestockV2ListProposal[] {
  return proposalsToAdd(list).filter((row) => proposalKind(row) === 'engine');
}

/**
 * 你已經有貨 — styles the engine did NOT pick that are holding pieces Magnus already paid for.
 * Kept in their own section, ABOVE the engine queue, because the gate now suppresses their 上架 and
 * 去拿 rows entirely: if one of these slipped below the engine queue's cap he would never see it,
 * and idle stock nobody is looking at is money. A different question, so a different section.
 */
export function stockProposals(list: RestockV2List | null): RestockV2ListProposal[] {
  return proposalsToAdd(list).filter((row) => proposalKind(row) === 'stock');
}

/** Pieces of this style he already owns: not out front at the counter, plus whatever is in the office. */
export function ownedPieces(row: RestockV2ListProposal): number {
  return (numberOr(row.not_displayed) || 0) + (numberOr(row.office_qty) || 0);
}

/**
 * 「門市未展示 3 件 · 辦公室 2 件」 — the quantity on the face of the card, because that IS the
 * decision. `office_qty` is the style's TOTAL office stock, which is why it is not called 去拿.
 *
 * Same no-fact-twice rule as the numbers line: the backend's stock `reason` already says 「門市有 3
 * 件未展示」 in words, so a part whose count is already in `shown` is dropped rather than echoed.
 */
export function ownedLine(row: RestockV2ListProposal, shown = ''): string {
  const seen = squash(shown);
  const parts: string[] = [];
  const said = (word: string, count: number) => seen.indexOf(word) >= 0 && seen.indexOf(`${count}件`) >= 0;
  const counter = numberOr(row.not_displayed);
  const office = numberOr(row.office_qty);
  if (counter !== null && counter > 0 && !said('未展示', counter)) {
    parts.push(`門市未展示${NB}${counter}${NB}件`);
  }
  if (office !== null && office > 0 && !said('辦公室', office)) {
    parts.push(`辦公室${NB}${office}${NB}件`);
  }
  return parts.join(`${NB}· `);
}

export function proposalsToRemove(list: RestockV2List | null): RestockV2ListProposal[] {
  return list?.proposals?.remove || [];
}

export function approvedRows(list: RestockV2List | null): RestockV2ListApproved[] {
  return list?.approved || [];
}

/** The tab badge: how many decisions are waiting for him. Zero on a backend without the table. */
export function pendingDecisions(plan: RestockV2Plan | null | undefined): number {
  const list = counterList(plan);
  if (!list || list.ready !== true) return 0;
  return proposalsToAdd(list).length + proposalsToRemove(list).length;
}

/** Over capacity only. Empty string when it fits — the warning must never be a permanent fixture. */
export function overLine(list: RestockV2List | null): string {
  const over = Number(list?.pieces?.over_by) || 0;
  return over > 0 ? `超出 ${over} 件` : '';
}

export function isOverCapacity(list: RestockV2List | null): boolean {
  return (Number(list?.pieces?.over_by) || 0) > 0;
}

/** 「計畫 148／150 件」 — the planned pieces of HIS list against the rail capacity. */
export function plannedLine(list: RestockV2List | null): string {
  const planned = Number(list?.pieces?.planned) || 0;
  const capacity = Number(list?.pieces?.capacity) || 0;
  return `${planned}／${capacity}`;
}

/**
 * 「清單秋冬 27%，本週目標 30%」. The server owns the sentence (`gap_line`); this only falls back to
 * the two numbers when it did not send one, so the ramp pressure is never silently missing.
 */
export function seasonGapLine(list: RestockV2List | null): string {
  const season = list?.season;
  if (!season) return '';
  const own = (season.gap_line || '').trim();
  if (own) return own;
  const share = Number(season.aw_share_pct);
  const target = Number(season.aw_target_pct);
  if (!Number.isFinite(share) || !Number.isFinite(target)) return '';
  return `清單秋冬 ${share}%，本週目標 ${target}%`;
}

/**
 * The banner under the stat tile. It must say what to DO — the tile already prints the number — but
 * the SERVER owns the sentence, because it names the actual styles the engine would drop first
 * (「把 3133 條紋口袋襯衫 換成秋款」) and the client cannot know which those are.
 *
 * A client-side version of this shipped for one round and was WRONG twice over:
 *   - `aw_share_pct` is AW/(AW+SS), but `pieces.planned` also counts all-season garments (長褲),
 *     which are in neither half — always the larger, wrong denominator. Prod: planned 104 vs the
 *     real aw_ss_total 64.
 *   - a SWAP moves the numerator up AND the summer denominator down, so it is worth about two
 *     pieces of a pure add; `(target − share)/100 × N` models an addition.
 * The two errors partly cancelled, which is exactly why the output looked plausible. Measured on
 * prod 2026-09-16: truth 3 pieces, that formula said 4. Hence: render, do not compute.
 *
 * Empty once the target is met — read off `gap_pieces`, never by parsing the sentence.
 */
export function seasonActionLine(list: RestockV2List | null): string {
  const season = list?.season;
  if (!season) return '';
  const gap = numberOr(season.gap_pieces);
  if (gap !== null && gap <= 0) return '';
  return seasonGapLine(list);
}

/** 建議帶回 is served ranked and only when over; the screen never re-ranks and never acts on it. */
export function bringBackHints(list: RestockV2List | null): RestockV2ListBringBack[] {
  return isOverCapacity(list) ? (list?.suggest_bring_back || []) : [];
}

// ---- one card's 依據 --------------------------------------------------------------------------

/** The sentence that is ALWAYS on the card. Never empty — an unexplained proposal is not answerable. */
export function evidenceLine(row: { lane_reason?: string | null; reason?: string | null }): string {
  const lane = (row.lane_reason || '').trim();
  if (lane) return lane;
  const reason = (row.reason || '').trim();
  return reason || '引擎沒有給依據';
}

/**
 * What the card LEADS with, and therefore the string stored on his decision.
 * A 建議換下 card is answering a different question — 「why should this leave?」 — so its exit-bucket
 * `reason` outranks the lane sentence that put it on the rail in the first place.
 */
export function proposalEvidence(
  row: { lane_reason?: string | null; reason?: string | null }, kind: ProposalKindKey,
): string {
  return proposalLines(row, kind).headline;
}

/**
 * The two 依據 lines of a card, in the order that answers ITS question.
 *  - 建議加入 / 你已經有貨 lead with the lane sentence; a 建議加入 row that ALSO holds stock shows
 *    the stock sentence under it, and a 你已經有貨 row has no lane, so the stock sentence leads.
 *  - 建議換下 leads with the exit bucket, and the lane that put it on the rail follows.
 * Both are always rendered — neither is ever behind a fold.
 */
export function proposalLines(
  row: { lane_reason?: string | null; reason?: string | null }, kind: ProposalKindKey,
): { headline: string; sub: string } {
  const lane = (row.lane_reason || '').trim();
  const reason = (row.reason || '').trim();
  const ordered = kind === 'remove' ? [reason, lane] : [lane, reason];
  const parts = ordered.filter((part) => !!part);
  const headline = parts[0] || '引擎沒有給依據';
  const sub = parts[1] && parts[1] !== headline ? parts[1] : '';
  return { headline, sub };
}

/**
 * 「線上7天 1 件 · 門市30天 2 件」 — only the counts the engine actually sent, and only the ones the
 * card is not already showing. `shown` is the 依據 text already on the card: a fact printed twice is
 * noise that makes the card look unread, so anything restated there is dropped here.
 *
 * `asked` is a 0|1 FLAG and is never rendered as a number — the badge carries that fact, so the
 * `ask_label` only earns a slot when the 依據 sentence has not already said 客人問過.
 */
export function evidenceNumbers(row: RestockV2ListProposal, shown = ''): string {
  const seen = squash(shown);
  const parts: string[] = [];
  const push = (text: string) => { if (!seen || seen.indexOf(squash(text)) < 0) parts.push(text); };
  const u14 = numberOr(row.online_u14);
  const u7 = numberOr(row.online_u7);
  const counter = numberOr(row.counter_sold30);
  const label = (row.ask_label || '').trim();
  if (u14 !== null) push(`線上14天${NB}${u14}${NB}件`);
  if (u7 !== null) push(`線上7天${NB}${u7}${NB}件`);
  if (counter !== null) push(`門市30天${NB}${counter}${NB}件`);
  if (label && seen.indexOf('客人問過') < 0) push(label.replace(/\s+/g, NB));
  // The separator is glued to the token before it, so a wrap never leaves a 「·」 starting a line.
  return parts.join(`${NB}· `);
}

/** Approved SKUs the engine can no longer name. Silence here would show a shorter list than counts. */
export function missingSkus(list: RestockV2List | null): string[] {
  return (list?.missing || []).filter((sku) => !!sku);
}

/**
 * 建議換下 can be 37 rows on a freshly seeded rail. Show a screenful, then 顯示全部 — a queue that
 * scrolls forever reads as 「the engine wants to gut my counter」 and stops being answerable.
 */
export const QUEUE_CAP = 12;

export function cappedQueue<T>(rows: T[], showAll: boolean): T[] {
  return showAll ? rows : rows.slice(0, QUEUE_CAP);
}

/**
 * 訂得到嗎 — and NOTHING here reads the 進貨店家 tag any more (Magnus 2026-09-18).
 *
 * The order of the three answers is the backend's own (`sourced = lane === 'warehouse' || has_link`,
 * noke-restock-supply.js:47), so the row and the plan can never disagree:
 *   1. the 台灣倉庫 lane is orderable by definition — see isWarehouseLane for why this comes FIRST;
 *   2. `has_link` is the real gate, and the name to prefer;
 *   3. `sourced` is the same answer under the shipped name, for a payload that carries only it.
 * A row with none of the three reads ORDERABLE. A missing fact is not evidence of a missing link,
 * and the old tag fallback here (`!!source_tag`) is exactly what printed 訂不到 on styles that order
 * fine — the single most misleading sentence on the screen.
 */
export function isSourced(row: {
  sourced?: boolean | null; has_link?: boolean | null; sku?: string | null; cat?: string | null;
}): boolean {
  if (isWarehouseLane(row)) return true;
  if (typeof row.has_link === 'boolean') return row.has_link;
  if (typeof row.sourced === 'boolean') return row.sourced;
  return true;
}

export const RAIL_LABEL = { on: '在櫃', off: '不在櫃' } as const;

export function railLabel(onRail?: boolean | null): string {
  return onRail ? RAIL_LABEL.on : RAIL_LABEL.off;
}

// ---- 不要這個顏色／尺寸 -----------------------------------------------------------------------
// Two different keys, deliberately:
//   `variantPendingKey` namespaces the busy flag, so a chip's × disables that chip and not the
//     row's 移除 (both would otherwise be keyed by the bare SKU).
//   `variantVetoKey` is the (sku, variant_id) identity the veto is matched on EVERYWHERE — the
//     chips, the 已排除 fold and the 衣架 tiles all read the same key, so a tile can never offer a
//     colour the chip says is out.

// One definition each, imported by BOTH callers: the 選品 chips (useCounterList) and the rack tiles
// / 今天 cards (useVeto, which also owns the optimistic window). Two copies of a URL is how the two
// surfaces drift apart.
export const VARIANT_REJECT_URL = '/api/noke/restock-v2/list/variant/reject';
export const VARIANT_READD_URL = '/api/noke/restock-v2/list/variant/readd';
// 留著 / 取消留著 — the same pair shape as reject/readd, and deliberately NOT a boolean toggle: two
// endpoints mean a double-tap is idempotent in the direction he tapped, not a flip-flop.
// 設為主打 / 取消主打 — one pinned colour per style. Same two-endpoint shape as the pairs above
// for the same reason: a double-tap is idempotent in the direction he tapped, not a flip-flop.
// Clearing takes the SKU alone, because the pin is unique per style on the server.
export const VARIANT_MAIN_URL = '/api/noke/restock-v2/list/variant/main';
export const VARIANT_UNMAIN_URL = '/api/noke/restock-v2/list/variant/unmain';
export const LIST_MAIN_LABEL = '設為主打';
export const LIST_UNMAIN_LABEL = '取消主打';
export const LIST_MAIN_BADGE = '主打';
// What a pin does NOT do, said once, where he sets it: it is a preference inside the engine's own
// ranking, not an override of 架上已有／不可訂. Buying a 10th navy because he pinned navy is the
// failure mode this whole feature sits next to.
export const LIST_MAIN_NOTE = '主打只是優先：架上已有或不可訂的時候還是會換色。';

// The 看全部 fold on a row's colour/size chips. 「其他」 and not 「更多」 on purpose: nothing is
// being added when he opens it — these colours were already on the list, just not the ones the
// engine would buy first (Magnus 2026-09-19: 「the default should not be all color and 尺寸」).
// 熱銷 — the ranking's own answer, shown so he does not have to pin a colour just to find out
// what the data says (Magnus 2026-09-20: 「why it doesn't set 主打 automatically prefilled
// already? it should already look at past customer order」). It IS looking; nothing said so.
// 主打 wins the badge slot when both apply: his decision outranks the measurement, which is the
// entire reason the pin exists.
export const LIST_TOP_BADGE = '熱銷';

// 照建議設定主打 — one tap fills in every approved style's 主打 from the ranking he is already
// looking at (Magnus 2026-09-20: 「just set it automatically. i will change if needed」). It is
// ADDITIVE and never overwrites: a style he has already pinned is left exactly as it is, which
// is what makes it safe to press twice or press after an hour of editing.
export const MAIN_BATCH_URL = '/api/noke/restock-v2/list/variant/main-batch';
export const MAIN_BATCH_KEY = '__main_batch';
export const MAIN_BATCH_LABEL = '照建議設定主打';
export const MAIN_BATCH_NOTE = '依銷量填上每款的主打顏色／尺寸；你已經設過的款不會被蓋掉。';

export interface MainBatchPick { sku: string; variant_id: number; color?: string | null; size?: string | null }

/**
 * Every 主打 on a row, whichever shape the server sent.
 *
 * `main_variants` (several, since 2026-09-20) when it is there, and the older single
 * `main_variant` when it is not. NOT cosmetic back-compat: the two repos deploy separately, so
 * for the length of a Railway rollout the page is new and the API is old — and without this the
 * badge would disappear from every pinned style for those few minutes, which reads exactly like
 * 「my pins were deleted」.
 */
export function mainVariantIds(row: RestockV2ListApproved): Array<number | string> {
  const many = (row.main_variants || []).map((pin) => pin.variant_id)
    .filter((id) => id !== null && id !== undefined) as Array<number | string>;
  if (many.length) return many;
  const one = row.main_variant?.variant_id;
  return one === null || one === undefined ? [] : [one];
}

/** The styles that would GAIN 主打 from one tap: approved, has a ranked pick, not pinned yet. */
export function mainBatchPicks(rows: RestockV2ListApproved[]): MainBatchPick[] {
  const out: MainBatchPick[] = [];
  (rows || []).forEach((row) => {
    if (mainVariantIds(row).length) return;                    // his decision wins, untouched
    (row.top_variants || []).forEach((pick) => {
      if (pick && pick.variant_id !== null && pick.variant_id !== undefined) {
        out.push({ sku: row.sku, variant_id: pick.variant_id, color: pick.color, size: pick.size });
      }
    });
  });
  return out;
}

export function mainBatchBody(clientRef: string, picks: MainBatchPick[]): {
  client_ref: string; picks: MainBatchPick[]; skip_pinned: boolean;
} {
  return { client_ref: clientRef, picks, skip_pinned: true };
}
export const LIST_TOP_NOTE: Record<string, string> = {
  mix30: '這款最近 30 天賣最好的顏色／尺寸',
  own_year: '這款最近 30 天沒賣，這是過去一年賣最好的',
  category: '這款還沒賣過，這是同類商品最熱門的',
};
export const LIST_TOP_NOTE_FALLBACK = '目前排序最前面的顏色／尺寸';

export const LIST_VARIANT_FOLD_OPEN = '其他';
export const LIST_VARIANT_FOLD_CLOSE = '只看建議';
export const VARIANT_KEEP_URL = '/api/noke/restock-v2/list/variant/keep';
export const VARIANT_UNKEEP_URL = '/api/noke/restock-v2/list/variant/unkeep';

export const variantPendingKey = (sku: string, variantId: number | string): string => `variant|${sku}|${variantId}`;

/**
 * The busy flag for 留著 / 取消留著, in its OWN namespace.
 *
 * Not `variantPendingKey`: 留著 and 不要這個顏色／尺寸 are opposite decisions about the same pair, so
 * sharing a key would grey 選品's ✕ chip while a 留著 taken at the rail is in flight — a control
 * disabled by an unrelated decision reads as broken.
 */
export const variantKeepPendingKey = (sku: string, variantId: number | string): string => `keep|${sku}|${variantId}`;

/**
 * The busy flag for 設為主打 / 取消主打, keyed by the STYLE.
 *
 * By the style and not the pair, unlike the two above: one pin per style means tapping 主打 on a
 * second colour while the first is in flight is two writes racing for the same row, so every chip
 * of that style greys together and the last tap cannot lose to the first.
 */
export const variantMainPendingKey = (sku: string, variantId?: number | string | null): string =>
  // Per CHIP since 2026-09-20: a style may hold several 主打, so a write on 黑色 must not
  // grey out 海軍藍's button on the same row.
  `main|${sku}|${variantId === undefined || variantId === null ? '' : variantId}`;

export function variantVetoKey(sku: string, variantId?: number | string | null): string {
  return `${sku}|${variantId === null || variantId === undefined ? '' : variantId}`;
}

/**
 * The chips of one 我的清單 row: live colours first, vetoed ones after, deduped on `variant_id`.
 *
 * Reads BOTH shapes the backend may ship — a single `variants` array flagged with `vetoed: true`,
 * or `variants` + a separate `vetoed_variants`. A row with neither returns [], and the row simply
 * renders no chip line: an approved style whose variants the engine cannot name is still a valid
 * row, and inventing a 「單一款」 chip for it would offer a veto against nothing.
 */
export function listVariants(row: RestockV2ListApproved): RestockV2ListVariant[] {
  const out: RestockV2ListVariant[] = [];
  const seen: Record<string, true> = {};
  const push = (variant: RestockV2ListVariant, vetoed: boolean) => {
    const id = variant?.variant_id;
    if (id === null || id === undefined) return;
    const key = String(id);
    if (seen[key]) return;
    seen[key] = true;
    out.push({ ...variant, vetoed: vetoed || variant.vetoed === true });
  };
  const served = (row.variants || []).filter((variant) => !!variant);
  served.filter((variant) => variant.vetoed !== true).forEach((variant) => push(variant, false));
  (row.vetoed_variants || []).filter((variant) => !!variant).forEach((variant) => push(variant, true));
  served.filter((variant) => variant.vetoed === true).forEach((variant) => push(variant, true));
  return out;
}

export function liveVariants(row: RestockV2ListApproved): RestockV2ListVariant[] {
  return listVariants(row).filter((variant) => variant.vetoed !== true);
}

export function vetoedVariantsOf(row: RestockV2ListApproved): RestockV2ListVariant[] {
  return listVariants(row).filter((variant) => variant.vetoed === true);
}

/**
 * Every 不要這個顏色／尺寸 in force, for the 已排除 fold.
 *
 * The SERVER's list is the source of truth, because a variant veto is independent of its style's
 * status (settled 2026-09-17): it survives 不要這款 → 加回 on the SKU, so a veto on a rejected or
 * 待決定 style exists with no `approved` row to derive it from. The fallback only exists so that a
 * backend which ships the chips but not the list still shows the fold instead of an empty one.
 */
export function excludedVariants(list: RestockV2List | null): RestockV2ListExcludedVariant[] {
  const served = (list?.excluded_variants || []).filter((row) => !!row && !!row.sku);
  if (served.length) return served;
  const out: RestockV2ListExcludedVariant[] = [];
  approvedRows(list).forEach((row) => vetoedVariantsOf(row).forEach((variant) => out.push({
    ...variant,
    sku: row.sku,
    title: row.title,
    image_url: row.image_url ?? null,
    cat: row.cat,
    season: row.season ?? null,
  })));
  return out;
}

/**
 * Every 留著 in force, for the 留著 fold under the rack.
 *
 * Server list only — no `approved`-derived fallback, unlike `excludedVariants`. A 留著 has no chip
 * in 選品 to derive one from, and inventing rows would be worse than an empty fold: this list is
 * the ONLY place a kept variant can be taken back from once its style has left the rail.
 */
export function keptVariants(list: RestockV2List | null): RestockV2ListExcludedVariant[] {
  return (list?.kept_variants || []).filter((row) => !!row && !!row.sku);
}

/** `sku|variant_id` → true for every 留著, so a tile can render the pin without re-scanning. */
export function variantKeepSet(list: RestockV2List | null): Record<string, true> {
  const out: Record<string, true> = {};
  keptVariants(list).forEach((row) => { out[variantVetoKey(row.sku, row.variant_id)] = true; });
  return out;
}

/** `sku|variant_id` → true. What the 衣架 tiles filter on, built from both halves. */
export function variantVetoSet(list: RestockV2List | null): Record<string, true> {
  const out: Record<string, true> = {};
  excludedVariants(list).forEach((row) => { out[variantVetoKey(row.sku, row.variant_id)] = true; });
  approvedRows(list).forEach((row) => vetoedVariantsOf(row)
    .forEach((variant) => { out[variantVetoKey(row.sku, variant.variant_id)] = true; }));
  return out;
}

/**
 * `variants_ready === false` = `noke_counter_list_variants` is not migrated yet. Absent means a
 * backend that predates the flag, which also ships no variants, so the chips are empty anyway —
 * only an explicit false turns the ✕ off. A control that can only answer 503 is worse than none.
 */
export function variantsReady(list: RestockV2List | null): boolean {
  return list?.variants_ready !== false;
}

/** false = `noke_counter_list_mains` is missing, so no chip may offer 設為主打. Absent reads ready,
 * so an older backend that does not ship the flag is not crippled by it. */
export function mainsReady(list: RestockV2List | null): boolean {
  return list?.mains_ready !== false;
}

/** A row with no `variant_id` can never be matched, so it is never treated as vetoed. */
export function isVariantVetoed(
  vetoed: Record<string, true>, sku: string, variantId?: number | null,
): boolean {
  if (variantId === null || variantId === undefined) return false;
  return !!vetoed[variantVetoKey(sku, variantId)];
}

// ---- 我的清單, grouped the way 衣架 groups --------------------------------------------------
// Same fixture model as the rack map: a style hangs on the rail its CATEGORY belongs to
// (sourcing.ts BUCKET, itself a mirror of the backend's DEFAULT_FIXTURES.allowed_categories).

export interface ApprovedGroup {
  id: string;
  rows: RestockV2ListApproved[];
  pieces: number;
}

export function groupApprovedByFixture(rows: RestockV2ListApproved[]): ApprovedGroup[] {
  const buckets = new Map<string, RestockV2ListApproved[]>();
  (rows || []).forEach((row) => {
    const id = sourcingBucket(row.cat);
    const list = buckets.get(id) || [];
    buckets.set(id, list);
    list.push(row);
  });
  return Array.from(buckets, ([id, group]) => ({
    id,
    rows: group,
    pieces: group.reduce((sum, row) => sum + (Number(row.depth) || 0), 0),
  }));
}

// ---- the optimistic window for the 選品 verbs -------------------------------------------------
// Magnus 2026-09-17: 「if i remove one, don't make it refresh everything」. Every write used to
// `await refetch()`, and /restock-v2 measures ~6.3s on prod — so one 移除 blanked the screen for six
// seconds, and his first curation pass is ~69 decisions. Seven minutes of spinner.
//
// So the tap now settles LOCALLY and the recompute is coalesced behind it. What that buys, and what
// it costs, are deliberately split:
//   - the ROW he acted on moves at once (this patch), because he knows what he just did;
//   - the derived AGGREGATES — depth, capacity, 秋冬 %, the 帶回 ranking, the Korea order — are NOT
//     re-derived here. Guessing them would be inventing numbers. They are marked 數字待重算 until
//     the real recompute lands. Showing a stale figure as if it were fresh is the failure mode this
//     avoids; showing it labelled is honest.

export interface ListPatch {
  // sku → the answer he just gave, before the server has confirmed it.
  decided: Record<string, CounterListStatus>;
  // sku → back to 待決定 (移除 on 我的清單, or 加回 on an 已排除 row: both DELETE the row).
  removed: Record<string, true>;
}

export const EMPTY_LIST_PATCH: ListPatch = { decided: {}, removed: {} };

export function listPatchIsEmpty(patch: ListPatch): boolean {
  return !Object.keys(patch.decided).length && !Object.keys(patch.removed).length;
}

/** Last answer wins: 加入清單 then 移除 on the same SKU must not leave both marks standing. */
export function mergeListPatch(patch: ListPatch, next: ListPatch): ListPatch {
  const decided = { ...patch.decided, ...next.decided };
  const removed = { ...patch.removed, ...next.removed };
  Object.keys(next.decided).forEach((sku) => { delete removed[sku]; });
  Object.keys(next.removed).forEach((sku) => { delete decided[sku]; });
  return { decided, removed };
}

/** Roll one write's own marks back after the server refused it. Other pending marks stay. */
export function dropListPatch(patch: ListPatch, next: ListPatch): ListPatch {
  const decided = { ...patch.decided };
  const removed = { ...patch.removed };
  Object.keys(next.decided).forEach((sku) => { delete decided[sku]; });
  Object.keys(next.removed).forEach((sku) => { delete removed[sku]; });
  return { decided, removed };
}

/** The row a just-approved proposal becomes, so 我的清單 gains it in the same frame it left the queue. */
function approvedFromProposal(row: RestockV2ListProposal): RestockV2ListApproved {
  return {
    sku: row.sku,
    title: row.title,
    image_url: row.image_url ?? null,
    cat: row.cat,
    season: row.season ?? null,
    tier: row.tier ?? null,
    source: 'recommended',
    depth: Number(row.depth) || 0,
    on_rail: row.on_rail ?? null,
    sourced: row.sourced ?? null,
    lane_reason: row.lane_reason || null,
    owner_pick: false,
  };
}

/**
 * The plan as it will look once the server agrees with the 選品 answers still in flight.
 *
 * Counts, pieces and season are deliberately LEFT ALONE — see the note above. `plan.order`,
 * `plan.today` and `plan.racks` are left alone for the same reason: a style leaving the list changes
 * the Korea order and the rack goal, and only the engine knows by how much.
 */
export function applyListPatch(
  plan: RestockV2Plan | null, patch: ListPatch,
): RestockV2Plan | null {
  if (!plan || !plan.list || listPatchIsEmpty(patch)) return plan;
  const list = plan.list;
  const touched = (sku: string) => !!patch.decided[sku] || !!patch.removed[sku];
  const queue = [...(list.proposals?.add || []), ...(list.proposals?.remove || [])];
  const proposalOf = (sku: string) => queue.filter((row) => row.sku === sku)[0] || null;

  // Anything he just answered leaves the yes/no queue at once — that is the whole point of the tap.
  const proposals = {
    add: (list.proposals?.add || []).filter((row) => !touched(row.sku)),
    remove: (list.proposals?.remove || []).filter((row) => !touched(row.sku)),
  };

  const approved = (list.approved || [])
    .filter((row) => patch.decided[row.sku] !== 'rejected' && !patch.removed[row.sku]);
  const already: Record<string, true> = {};
  approved.forEach((row) => { already[row.sku] = true; });
  Object.keys(patch.decided)
    .filter((sku) => patch.decided[sku] === 'approved' && !already[sku])
    .forEach((sku) => {
      const row = proposalOf(sku);
      if (row) approved.push(approvedFromProposal(row));
    });

  // 已排除 reads plan.excluded.plan, so a 不要這款 answered in 選品 has to land there too, or the
  // style would simply vanish with no 加回 anywhere on the screen.
  const rejectedNow: RestockV2ExcludedStyle[] = Object.keys(patch.decided)
    .filter((sku) => patch.decided[sku] === 'rejected')
    .map((sku) => {
      const row = proposalOf(sku) || (list.approved || []).filter((item) => item.sku === sku)[0] || null;
      return {
        sku,
        title: row ? row.title : null,
        image_url: row ? (row.image_url ?? null) : null,
        cat: row ? row.cat : null,
        season: row ? (row.season ?? null) : null,
        reason: null,
        created_by: null,
        created_at: null,
        note: null,
      };
    });

  const excluded = {
    plan: (plan.excluded?.plan || [])
      .filter((row) => !patch.removed[row.sku] && patch.decided[row.sku] !== 'approved')
      .concat(rejectedNow),
    today: plan.excluded?.today || [],
  };

  return {
    ...plan,
    excluded,
    list: { ...list, proposals, approved },
  };
}

// ---- request bodies ---------------------------------------------------------------------------
// The tier and the 依據 string he saw are stored WITH the decision, so a later 「why is 3588 on the
// list?」 is answered by the row itself rather than by re-running the engine.

export function decideBody(
  clientRef: string, sku: string, decision: CounterListDecision, tier?: TierKey | null,
  reason?: string | null,
): RestockV2ListDecideBody {
  return { client_ref: clientRef, sku, decision, tier: tier ?? null, reason: (reason || '').trim() || null };
}

/**
 * 全部照建議: ONE call for the whole queue, never N calls — it is one transaction on the server and
 * one undo in his head. Additive only, so `decision` is hard-coded to 'approve'.
 */
export function decideBatchBody(clientRef: string, rows: RestockV2ListProposal[]): RestockV2ListBatchBody {
  return {
    client_ref: clientRef,
    decisions: (rows || []).map((row) => ({
      sku: row.sku, decision: 'approve' as const, tier: row.tier ?? null,
      reason: (evidenceLine(row) || '').trim() || null,
    })),
  };
}

export function listSkuBody(clientRef: string, sku: string): RestockV2ListSkuBody {
  return { client_ref: clientRef, sku };
}

export function listSeedBody(clientRef: string): RestockV2ListSeedBody {
  return { client_ref: clientRef };
}

/**
 * 不要這個顏色／尺寸. `variant_id` is the key the server matches on; the colour and size are sent
 * for DISPLAY only (the veto table denormalises them so 已排除 reads without a join). Measured on
 * prod 2026-09-17: every `noke_inventory` row carries a non-null `variant_id`, so the key is safe.
 */
export function variantVetoBody(
  clientRef: string, sku: string, variant: RestockV2ListVariant, reason?: string | null,
): RestockV2ListVariantBody {
  return {
    client_ref: clientRef,
    sku,
    variant_id: variant.variant_id,
    color: variant.color ?? null,
    size: variant.size ?? null,
    reason: (reason || '').trim() || null,
  };
}

export function variantReaddBody(
  clientRef: string, sku: string, variantId: number,
): RestockV2ListVariantReaddBody {
  return { client_ref: clientRef, sku, variant_id: variantId };
}

/**
 * 留著 — 「it stays on the rail」. Same body as the veto (the colour and size ride along so the
 * 留著 fold can name the row without a join), a different endpoint, and the opposite meaning: the
 * engine stops proposing 帶回 for this pair. It does NOT order it and does not touch `target`.
 */
export function variantKeepBody(
  clientRef: string, sku: string, variant: RestockV2ListVariant, reason?: string | null,
): RestockV2ListVariantBody {
  return {
    client_ref: clientRef,
    sku,
    variant_id: variant.variant_id,
    color: variant.color ?? null,
    size: variant.size ?? null,
    reason: (reason || '').trim() || null,
  };
}

/** 設為主打. Same body as a veto — the server re-resolves color/size from the catalogue. */
export function variantMainBody(
  clientRef: string, sku: string, variant: RestockV2ListVariant, reason?: string | null,
): RestockV2ListVariantBody {
  return {
    client_ref: clientRef,
    sku,
    variant_id: variant.variant_id,
    color: variant.color ?? null,
    size: variant.size ?? null,
    reason: (reason || '').trim() || null,
  };
}

/** 取消主打. The SKU is the whole key — noke_counter_list_mains is keyed on it. */
export function variantUnmainBody(
  clientRef: string, sku: string, variantId?: number | string | null,
): { client_ref: string; sku: string; variant_id?: number | string } {
  // `variant_id` present = drop THAT 主打; absent = drop every 主打 on the style, which is what
  // the verb meant while one style could only hold one.
  const body: { client_ref: string; sku: string; variant_id?: number | string } = { client_ref: clientRef, sku };
  if (variantId !== undefined && variantId !== null && variantId !== '') body.variant_id = variantId;
  return body;
}

/** 取消留著. The (sku, variant_id) pair is the whole key, as it is for 加回. */
export function variantUnkeepBody(
  clientRef: string, sku: string, variantId: number,
): RestockV2ListVariantReaddBody {
  return { client_ref: clientRef, sku, variant_id: variantId };
}

/** The picker accepts `{ results: [...] }` or a bare array, so a shape change is not a blank box. */
export function searchRows(payload: unknown): RestockV2ListSearchRow[] {
  if (Array.isArray(payload)) return payload as RestockV2ListSearchRow[];
  const rows = (payload as { results?: unknown } | null | undefined)?.results;
  return Array.isArray(rows) ? (rows as RestockV2ListSearchRow[]) : [];
}

/** 已在清單 / 已排除 / nothing — what a search hit already is, so 加入 is never a surprise. */
export function searchRowState(row: RestockV2ListSearchRow): string {
  if (row.on_list === 'approved') return '已在清單';
  if (row.on_list === 'rejected') return '已排除';
  return '';
}

/**
 * What 加入 actually costs. A hit that is already physically at the counter is a free decision;
 * one with nothing on hand puts pieces on the next Korea order. Same tap, different money — so the
 * row says which before he taps, rather than after the order copy is generated.
 * Empty when the picker did not report stock: silence beats a guessed zero.
 */
export function stockNote(row: RestockV2ListSearchRow): string {
  const onHand = numberOr(row.on_hand);
  if (onHand === null) return '';
  return onHand > 0 ? `門市已有 ${onHand} 件` : '門市沒有，加入後會進訂單';
}

export function isFreeToAdd(row: RestockV2ListSearchRow): boolean {
  return (numberOr(row.on_hand) || 0) > 0;
}

// ---- 看更多: 引擎沒選的 ------------------------------------------------------------------------
// Magnus 2026-09-17, looking at the screen: 「建議加入 only have 2 items … i need more, show me
// suggestions and i will decide whether to add」. Measured the same day: 建議加入 is bounded by his
// own `new_styles_per_week` dial — 8 gives 2 proposals, 20 gives 17, 30 (the max) gives 19, where
// the evidence floor takes over. So the cap is the dial, not the catalogue, and he wants to see past
// it and judge for himself.
//
// This section is therefore NOT more of 建議加入. 建議加入 is 「引擎這週選的」; this is 「引擎沒選，
// 但你可以自己挑」 — every row here is BELOW the engine's own evidence bar, and the copy says so
// three times over (title, note, intro) because the whole point is that he is overriding the engine
// knowingly. A row whose evidence is 「線上 14 天 0 件」 says exactly that on its face.
//
// What is deliberately ABSENT: any bulk accept. 全部照建議 exists on 建議加入 because every row in
// that queue cleared the engine's bar. Here none of them did, and a 全選 would be the 2026-09-15
// mistake (16 evidence-free styles into a Korea order) wearing a friendlier button. One tap, one
// style, for ever.

export const POOL_TITLE = '引擎沒選的';
export const POOL_NOTE = '引擎沒挑，你可以自己挑';
export const POOL_INTRO = '上面「建議加入」是引擎這週選的。這一區是引擎沒選的——證據比較弱，或這週的新款額度已經滿了。每一款的依據都寫在卡片上，你自己看過再決定。';
export const POOL_NO_BATCH_NOTE = '這一區沒有「全部照建議」。引擎沒有幫這些背書，所以一款一款按。';
export const POOL_BADGE = '引擎沒選';
export const POOL_OPEN_LABEL = '看更多可以挑的款';
export const POOL_CLOSE_LABEL = '收起';
export const POOL_MORE_LABEL = '看更多';
export const POOL_EMPTY = '沒有其他可以挑的款了，引擎已經把能提的都提了。';
export const POOL_END = '已經到最後一款了。';
export const POOL_ERROR = '拿不到更多款，請重試。';
export const POOL_RETRY_LABEL = '重試';
export const POOL_NOT_READY = '選品清單還沒建立，這一區先停用。';

// The view cut. The server defaults to 「有需求，或上架 90 天內的秋款」 — 457 of 1144 undecided
// styles on prod (2026-09-17). 687 styles behind a silent filter, on the screen he opened BECAUSE
// he was only shown two, would be the same complaint again — so the cut is named and the rest is
// one tap away, with both counts printed.
export const POOL_SCOPE_RELEVANT = '比較有機會的';
export const POOL_SCOPE_ALL = '全部';
export const POOL_SCOPE_NOTE = '預設只看比較有機會的款：有人買過、有人問過，或剛上架的秋款。其餘的按「全部」。';

// ---- the two filter axes -----------------------------------------------------------------------
// 1144 undecided styles is not a queue, it is a wall. The filters are what turn it into sittings:
// 「今天把 MdoC 的 22 款清掉」 is a job; 「看完 1144 款」 is not.
//
// CATEGORY CHIPS ARE ORDERED BY THE RACK HE WALKS, not alphabetically and not by count. He plans by
// fixture — 衣架 → 褲架 → 配件層架 → MdoC 島台 — and the same categories bucket onto those rails in
// `sourcing.ts` BUCKET (上衣/外套 → 衣架, 褲子 → 褲架, 配件 → 配件層架, MdoC → 島台), which is also
// where the pieces physically land when the box arrives. The chip KEEPS the server's own group name
// as its text (that is the word he uses when he looks at a garment) and the rack order is stated
// once underneath, rather than pinned onto six chips as 「上衣 · 左側衣架」.
export const POOL_SEASON_LABEL: Record<string, string> = { AW: '秋冬', SS: '夏', ALL: '全季款' };
export const POOL_SEASON_ORDER: PoolSeason[] = ['AW', 'SS', 'ALL'];
export const POOL_CAT_ORDER = ['上衣', '外套', '褲子', '配件', 'MdoC', '其他'];
export const POOL_CAT_NOTE = '順序照你走的架子：衣架（上衣・外套）→ 褲架 → 配件層架 → MdoC 島台。';
export const POOL_FILTER_ALL = '不分';
export const POOL_FILTER_CLEAR = '清掉篩選';
export const POOL_FILTER_EMPTY = '這個範圍沒有可以挑的款。換一個篩選，或按「全部」把範圍放大。';

/** The rack groups to draw chips for: the server's own list when it sends one, else the known order. */
export function poolCatChips(groups?: string[] | null): string[] {
  const served = (groups || []).filter((name) => !!name);
  if (!served.length) return POOL_CAT_ORDER;
  // Served names, in WALK order; anything the client does not know about is appended rather than
  // dropped, so a group added on the server still gets a chip.
  const known = POOL_CAT_ORDER.filter((name) => served.indexOf(name) >= 0);
  return known.concat(served.filter((name) => known.indexOf(name) < 0));
}

/**
 * 「現在這個範圍：22 款」 — the size of the cut he is looking at, from the server's own `total` for
 * the query it just served. Always rendered, because a filter he cannot price is a filter he cannot
 * plan a sitting around.
 */
export function poolRangeLine(remaining: number): string {
  return `現在這個範圍：${remaining} 款`;
}

/**
 * A count for ONE chip, or null when the server did not send facets.
 *
 * Null means 「not measured」 and the chip then carries no number at all. The alternative — printing
 * a count measured under some other filter, or one this session did not ask for — is a stale number
 * shown as current, which is the failure this screen exists to avoid.
 */
export function poolFacetCount(
  facets: RestockV2ListPoolFacets | null | undefined, axis: 'season' | 'cat', key: string,
): number | null {
  const row = facets?.[axis];
  if (!row) return null;
  const value = row[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * A Korea row that SELLS but has no 下單連結.
 *
 * A style with real demand and no link is a job, not a dead end: the link is added where links are
 * added, and inventing a destination here would be worse than naming the fix. Everything else with
 * no link keeps the plain 訂不到 sentence.
 *
 * M01 is NOT this card any more, and that is the point. It was the worked example for the old
 * tag-shaped wording — 42 units in 14 days, 「只差進貨店家」 — but M01 is MdoC, 台灣倉庫 stock that is
 * never ordered from Korea, so there was no gap to fix and nothing to tell him to go and do. It is
 * orderable, full stop, and `isWarehouseLane` takes it out of this question entirely.
 */
export const POOL_FIXABLE_LINK = '賣得動，只差下單連結：補上連結就能訂';

export function poolLinkIsFixable(item: RestockV2ListPoolItem): boolean {
  if (isWarehouseLane(item)) return false;
  const unlinked = !!(item.sourced_label || '').trim() || item.sourced === false;
  return unlinked && item.has_demand === true;
}

/** How many rows one 看更多 adds. Small on purpose: the pool is hundreds, and a batch he cannot finish reading is the same as no batch. */
export const POOL_BATCH = 12;

/**
 * A pool row drawn as a proposal card. The two sentences swap into the card's two 依據 slots:
 *   headline ← `evidence_line`  the style's own evidence (「線上 14 天 0 件」), which is what he judges on
 *   sub      ← `lane_reason`    why the engine passed (「新季款每週 8 款已滿」)
 * so `proposalLines(row, 'pool')` needs no special case — it already leads with the lane field.
 *
 * `depth` is 0 and never rendered: the engine did not plan this style, so there IS no piece count,
 * and printing 「引擎放 0 件」 would be inventing an engine opinion that does not exist.
 */
export function poolProposal(item: RestockV2ListPoolItem): RestockV2ListProposal {
  // Read 訂不到 off the SERVER's own sentence, not off the boolean: `sourced_label` is non-null on
  // exactly the rows that cannot be ordered (129 of 1144 on prod, measured 2026-09-17), and a
  // present label must win over any disagreeing flag.
  //
  // …EXCEPT on the 台灣倉庫 lane, where the label itself is the thing that is wrong. An MdoC row has
  // no `product_links` row and never needs one, so a stale backend still labelling it 訂不到 must not
  // be able to push that claim onto the card.
  const unsourced = !isWarehouseLane(item) && !!(item.sourced_label || '').trim();
  return {
    sku: item.sku,
    title: item.title,
    image_url: item.image_url ?? null,
    cat: item.cat,
    season: item.season ?? null,
    tier: item.tier ?? null,
    depth: 0,
    lane_reason: item.evidence_line || '',
    reason: item.lane_reason || null,
    online_u14: item.online_u14 ?? null,
    online_u7: item.online_u7 ?? null,
    counter_sold30: item.counter_sold30 ?? null,
    asked: item.asked ?? null,
    ask_label: item.ask_label ?? null,
    source_tag: item.source_tag ?? null,
    has_link: item.has_link ?? null,
    // 台灣倉庫 is orderable by definition; below that, the label wins, then the flag.
    sourced: isWarehouseLane(item) ? true : (unsourced ? false : (item.sourced ?? null)),
    on_rail: item.on_rail ?? null,
    listed_days: item.listed_days ?? null,
  };
}

/**
 * How the SECOND line of a pool card is drawn. Verified across all 1144 prod rows (2026-09-17):
 * 924 `lane_rule`, 218 `no_evidence`, 2 `engine_pick`, 0 unknown.
 *
 * These must not look alike, and the difference is not decoration. M01 sells 42 units in 14 days
 * and is out ONLY because 全季款 do not enter the new-season lane — 「引擎沒選的原因：全季款，不進
 * 新季款」 is a rule, and drawing it in warning ochre would tell him a strong seller is a weak
 * candidate. A row that failed the evidence floor IS a weak candidate, and gets the ochre.
 */
export function poolSubTone(item: RestockV2ListPoolItem): 'muted' | 'rule' | 'warn' {
  if (item.block_kind === 'lane_rule') return 'rule';
  if (item.block_kind === 'no_evidence') return 'warn';
  return 'muted';
}

/** The 依據 chip of a pool row, stored on his decision. Never empty — an unexplained row is not answerable. */
export function poolEvidence(item: RestockV2ListPoolItem): string {
  return (item.evidence_line || '').trim() || '引擎沒有給依據';
}

/**
 * Batches, joined and deduped by SKU.
 *
 * The backend's ranking is stable within one `pool_version` (score desc, online_u14 desc, sku asc)
 * and it re-snapshots about every 60 seconds, so two pages requested either side of a bump can
 * overlap. Offsets alone would then show the same style twice; the SKU is the identity, so the SKU
 * is what dedupes. First occurrence wins — the earlier page is the one he has already read.
 */
export function mergePoolItems(
  older: RestockV2ListPoolItem[], next: RestockV2ListPoolItem[],
): RestockV2ListPoolItem[] {
  const out: RestockV2ListPoolItem[] = [];
  const seen: Record<string, true> = {};
  [...(older || []), ...(next || [])].forEach((item) => {
    if (!item || !item.sku || seen[item.sku]) return;
    seen[item.sku] = true;
    out.push(item);
  });
  return out;
}

/**
 * What the section actually renders.
 *
 * `decided` is the pool's OWN record of the rows he has answered, and it is why an answer here
 * survives a recompute. The pool is NOT refetched when /restock-v2 lands, so a row hidden by the
 * shared optimistic patch would come back the moment that patch was retired — which is exactly the
 * bug Magnus hit on the wholesale clear (「why it reverted back」), just on a different surface.
 * Once answered, a pool row is gone until the pool itself is reloaded.
 *
 * `hidden` is the same style decided somewhere ELSE on the screen (建議加入, the picker, 我的清單).
 * It only ever removes more rows, so it cannot resurrect one.
 */
export function poolVisible(
  items: RestockV2ListPoolItem[], decided: Record<string, true>, hidden?: Record<string, true>,
): RestockV2ListPoolItem[] {
  return (items || []).filter((item) => !decided[item.sku] && !(hidden || {})[item.sku]);
}

/** 「還有 N 款」 — the server's total minus what he has answered here. Never negative, never a guess. */
export function poolRemaining(total: number, decidedCount: number): number {
  const left = (Number(total) || 0) - (Number(decidedCount) || 0);
  return left > 0 ? left : 0;
}

/** The opener's label carries the count, so nothing is hidden behind a number he cannot see. */
export function poolOpenLabel(remaining: number): string {
  return remaining > 0 ? `${POOL_OPEN_LABEL}（還有 ${remaining} 款）` : POOL_OPEN_LABEL;
}

/** The next-batch label says what it will cost and what is left behind it. */
export function poolMoreLabel(shown: number, remaining: number): string {
  const left = remaining - shown;
  return left > 0 ? `${POOL_MORE_LABEL}（還有 ${left} 款）` : POOL_MORE_LABEL;
}

/** 在櫃 rows appear in the pool too (undecided ≠ off the rail), so the row says which it is. */
export function poolStockNote(item: RestockV2ListPoolItem): string {
  const onHand = numberOr(item.on_hand);
  if (onHand === null || onHand <= 0) return '';
  return `門市已有 ${onHand} 件`;
}

/** The pool payload, read defensively: a shape change must degrade to an empty section, never throw. */
export function poolItems(payload: unknown): RestockV2ListPoolItem[] {
  const rows = (payload as { items?: unknown } | null | undefined)?.items;
  return Array.isArray(rows) ? (rows as RestockV2ListPoolItem[]).filter((row) => !!row && !!row.sku) : [];
}

// ---- 加入商品: type a SKU, press Enter ---------------------------------------------------------
// Magnus 2026-09-18: 「at the 選品 tab make it allow me to enter sku, to 加入清單 manually」.
//
// It already half-existed and he could not find it — which is the same as it not existing. The box
// sat BELOW 你已經有貨 / 建議加入 / 看更多 / 建議換下, thousands of pixels down once 看更多 is open,
// and it made him search for a SKU he had already typed in full. Both halves of that are fixed on
// the screen (placement) and here (the exact-SKU path resolves and writes in one keystroke).
//
// Everything below is pure so the five outcomes can be asserted without rendering: 'added' is the
// only one that writes, and the two that REFUSE to write — 'already' and 'rejected' — are the point
// of resolving before adding at all. A silent re-add of a style he threw off the list would undo a
// decision he made on purpose, and the screen would never mention it.

/** `3588`, `M01` — the two SKU shapes the catalogue and the counter actually use. */
export const SKU_SHAPE = /^M?\d{1,10}$/i;

export function looksLikeSku(query: string): boolean {
  return SKU_SHAPE.test(String(query ?? '').trim());
}

/**
 * The row whose SKU IS what he typed — case-insensitively, because he types 'm01' and the catalogue
 * stores 'M01'. Deliberately exact and never 「the first hit」: the picker matches on SKU PREFIX, so
 * 「358」 returns 3580…3589 and auto-adding the first of those would put a style he never named on
 * the list.
 */
export function exactSearchHit(
  rows: RestockV2ListSearchRow[], query: string,
): RestockV2ListSearchRow | null {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return null;
  return (rows || []).filter((row) => String(row?.sku ?? '').trim().toLowerCase() === q)[0] || null;
}

// The four sentences the outcomes carry. Each says what happened AND what to do next — a message
// that only says what failed leaves him holding the same SKU with nowhere to put it.
export const ADD_ALREADY_NOTE = '這款已經在清單上了，不用再加一次。往下看「我的清單」就找得到。';
export const ADD_REJECTED_NOTE = '這款你之前按過「不要這款」，所以引擎不再推薦它。要放回清單就按「加回清單」。';
export const ADD_BACK_LABEL = '加回清單';
export const ADD_UNDO_LABEL = '取消加入';
export const ADD_TITLE = '加入商品';
// The example lives in the HINT, not the placeholder: at 400px the placeholder carrying it was cut
// off mid-character inside the input, which is worse than not showing it at all.
export const ADD_NOTE = '知道商品編號（例：3588、M01）就直接輸入，按 Enter 就加入清單；不知道編號就打名稱，會列出符合的款讓你挑。';
export const ADD_PLACEHOLDER = '商品編號或名稱';
export const ADD_INPUT_LABEL = '輸入商品編號或名稱加入清單';
export const ADD_BUTTON_LABEL = '加入清單';

export const ADD_DELISTED_FALLBACK = (sku: string): string =>
  `${sku} 已經下架了，線上沒有上架、門市也沒有庫存，沒辦法加入清單。要放到門市就得先把商品重新上架。`;

export const ADD_MISSING_FALLBACK = (sku: string): string =>
  `找不到商品編號 ${sku}。檢查一下編號有沒有打錯，或改打商品名稱搜尋。`;

/** The name-search path. Says why there was no direct add, so the list below is not a surprise. */
export const ADD_HITS_NOTE = (query: string, count: number): string => (
  looksLikeSku(query)
    ? `沒有商品編號正好是 ${query}，下面 ${count} 款是編號或名稱相符的，挑一款按「加入」。`
    : `找到 ${count} 款，挑一款按「加入」。`
);

/**
 * The confirmation line under a just-added style. It repeats the ONE fact that changes what he can
 * do with it next — 訂得到嗎 — using `isSourced`, which reads the 下單連結 and the 台灣倉庫 lane and
 * never the 進貨店家 tag. MdoC is Taiwan warehouse stock and is never ordered from Korea, so it must
 * not be labelled 訂不到 here any more than anywhere else on the screen.
 */
export function addedNote(row: RestockV2ListSearchRow): string {
  const stock = stockNote(row);
  const supply = isSourced(row) ? '' : `${LIST_NO_LINK_WARNING}。`;
  return `已加入清單。${supply}${stock ? `${stock}。` : ''}`.trim();
}

/**
 * The HTTP status behind a refused write. 409 = the style exists and is 下架; 404 = there is no such
 * SKU. Two different problems, and only one of them is a typo — so the screen must be able to tell
 * them apart, and the sentence alone cannot carry that. 0 when the request never reached the server.
 */
export function httpStatusOf(error: unknown): number {
  const status = (error as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return Number(status) || 0;
}
